import {
  ILeaderboardReferral,
  ITransaction,
  ITransactionPoint,
  ITransactionWallet,
  LeaderboardReferralType,
  UserRank,
} from '@/redux/reducers/web3/web3.type'

import { DonatePointsType, IUserReferral, ReferralType, TAssets, TWallet } from './wallet.type'
import { w3Api } from '../w3Api'
import { W3ResponseType } from '../events'

export const walletEndpoints = {
  getBalance: 'wallet/balance',
  getDeposit: 'token/supported/deposit',
  getTransition: 'transaction',
  getWallet: '/wallet/offchain',
  getAssetsBalance: '/wallet/offchain/assets',
  donatePoints: '/donate-points',
  getLeaderboardReferral: '/promotions/referrals/leaderboard',
  getStatisticsReferral: '/promotions/referral-statistics',
  getListReferral: '/promotions/referrals',
}

const walletApiW3 = w3Api.injectEndpoints({
  endpoints: (build) => ({
    getBalance: build.query<any, { walletAddress: string }>({
      query: (args) => {
        return {
          url: walletEndpoints.getBalance,
          method: 'GET',
          params: { walletAddress: args.walletAddress },
        }
      },
    }),

    getDeposit: build.query({
      query: () => {
        return {
          url: walletEndpoints.getDeposit,
          method: 'GET',
        }
      },
    }),

    getTransition: build.query<W3ResponseType<ITransaction[]>, ITransactionWallet | ITransactionPoint>({
      query: (args) => {
        return {
          url: walletEndpoints.getTransition,
          method: 'GET',
          params: args,
        }
      },
      providesTags: (result, err, arg): any =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ id }: { id: string }) => ({ type: 'Transaction' as const, id: id }))
                : []),
              {
                type: 'Transaction',
                userID: (arg as ITransactionPoint)?.userId || 'default',
                walletAddress: (arg as ITransactionWallet).walletAddress,
              },
              { type: 'Transaction' },
            ]
          : [{ type: 'Transaction' }, { type: 'Transaction' }],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${(queryArgs as ITransactionPoint)?.userId || 'default'}-${(queryArgs as ITransactionWallet).walletAddress || 'default'}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 0) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),

    donatePoints: build.mutation<W3ResponseType, DonatePointsType>({
      query: ({ userId, points }) => {
        return {
          url: walletEndpoints.donatePoints,
          method: 'POST',
          body: {
            userId,
            points,
          },
        }
      },
    }),

    getWallet: build.query<
      TWallet,
      { id?: string; limit: number; ownerId: string; page: number; sort?: 'ASC' | 'DESC' }
    >({
      query: (args) => {
        return {
          url: walletEndpoints.getWallet,
          method: 'GET',
          params: {
            page: args.page,
            limit: args.limit,
            ownerId: args.ownerId,
            sort: args.sort,
            id: args.id,
          },
        }
      },
    }),
    getAssetsBalance: build.query<
      TAssets,
      { id?: string; limit: number; ownerId: string; page: number; sort?: 'ASC' | 'DESC' }
    >({
      query: (args) => {
        return {
          url: walletEndpoints.getAssetsBalance,
          method: 'GET',
          params: {
            page: args.page,
            limit: args.limit,
            ownerId: args.ownerId,
            sort: args.sort,
            id: args.id,
          },
        }
      },
    }),

    getLeaderboardReferral: build.query<ILeaderboardReferral<UserRank[]>, LeaderboardReferralType>({
      query: (args) => {
        return {
          url: walletEndpoints.getLeaderboardReferral,
          method: 'GET',
          params: args,
        }
      },
    }),

    getListReferral: build.query<W3ResponseType<IUserReferral[]>, any>({
      query: (args) => {
        return {
          url: walletEndpoints.getListReferral,
          method: 'GET',
          params: args,
        }
      },
      providesTags: (result): any =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ userId }: { userId: string }) => ({
                    type: 'Leaderboard' as const,
                    id: userId,
                  }))
                : []),
              { type: 'Leaderboard' },
            ]
          : [{ type: 'Leaderboard' }, { type: 'Leaderboard' }],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${(queryArgs as LeaderboardReferralType)?.dateFrom || 'default'}-${(queryArgs as LeaderboardReferralType)?.dateTo || 'default'}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 0) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),

    getStatisticsReferral: build.query<ReferralType, Record<string, never>>({
      query: () => {
        return {
          url: walletEndpoints.getStatisticsReferral,
          method: 'GET',
        }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetBalanceQuery,
  useGetDepositQuery,
  useGetTransitionQuery,
  useGetAssetsBalanceQuery,
  useGetWalletQuery,
  useGetLeaderboardReferralQuery,
  useDonatePointsMutation,
  useGetStatisticsReferralQuery,
  useGetListReferralQuery,
} = walletApiW3
